import { Button } from "@/components/ui/button";
import { Copy, Share2, Facebook, MessageCircle, MessageSquare, Image as ImageIcon, Linkedin, Loader2 } from "lucide-react";
import { XIcon } from "@/components/ui/icons/XIcon";
import { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { useIsMobile } from "@/hooks/use-mobile";
import { 
  generateShareText, 
  generateShareUrl, 
  generateTwitterUrl,
  generateFacebookUrl,
  generateLinkedInUrl,
  generateWhatsAppUrl,
  generateSMSUrl,
  copyToClipboard,
  getPlatformShareCapabilities,
  formatEmojiGrid,
  type ShareOptions 
} from "@/utils/shareEngine";
import { generateShareImage, downloadShareImage, shareGeneratedImage } from "@/utils/shareImageGenerator";
import { AICaptionGenerator } from "./AICaptionGenerator";
import { trackShareEvent } from "@/utils/shareAnalytics";
import { ShareTroubleshoot } from "./ShareTroubleshoot";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { ChevronDown } from "lucide-react";

interface ShareSheetProps {
  isOpen: boolean;
  onClose: () => void;
  shareOptions: ShareOptions;
}

export function ShareSheet({ isOpen, onClose, shareOptions }: ShareSheetProps) {
  const { toast } = useToast();
  const isMobile = useIsMobile();
  const [copied, setCopied] = useState(false);
  const [customCaption, setCustomCaption] = useState("");
  const [isGeneratingImage, setIsGeneratingImage] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const capabilities = getPlatformShareCapabilities();

  const getShareTextWithCaption = () => {
    const baseText = generateShareText(shareOptions);
    if (customCaption) {
      return `${customCaption}\n\n${baseText}`;
    }
    return baseText;
  };

  const handleCopy = async () => {
    const shareText = getShareTextWithCaption();
    const success = await copyToClipboard(shareText);
    
    if (success) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      
      trackShareEvent('copy', {
        puzzleNumber: shareOptions.puzzleNumber,
        won: shareOptions.won || false,
        attempts: shareOptions.attempts || 0,
        hadCaption: !!customCaption,
      });
      
      toast({
        title: "Copied!",
        description: "Ready to paste anywhere",
      });
    }
  };

  const handleNativeShare = async () => {
    const shareText = getShareTextWithCaption();
    const shareUrl = generateShareUrl(shareOptions.puzzleNumber);
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: "Wormble — Daily Word Puzzle",
          text: shareText,
          url: shareUrl,
        });
        
        trackShareEvent('native_share', {
          puzzleNumber: shareOptions.puzzleNumber,
          won: shareOptions.won || false,
          attempts: shareOptions.attempts || 0,
          hadCaption: !!customCaption,
        });
        onClose();
      } catch (err) {
        if ((err as Error).name !== "AbortError") {
          handleCopy();
        }
      }
    } else {
      handleCopy();
    }
  };

  const handleTwitterShare = () => {
    const shareText = getShareTextWithCaption();
    const shareUrl = generateShareUrl(shareOptions.puzzleNumber);
    const twitterUrl = generateTwitterUrl(shareText, shareUrl);
    
    trackShareEvent('twitter', {
      puzzleNumber: shareOptions.puzzleNumber,
      won: shareOptions.won || false,
      attempts: shareOptions.attempts || 0,
      hadCaption: !!customCaption,
    });
    
    window.open(twitterUrl, "_blank", "noopener,noreferrer,width=550,height=420");
  };

  const handleFacebookShare = () => {
    const shareUrl = generateShareUrl(shareOptions.puzzleNumber);
    const facebookUrl = generateFacebookUrl(shareUrl);
    
    trackShareEvent('facebook', {
      puzzleNumber: shareOptions.puzzleNumber,
      won: shareOptions.won || false,
      attempts: shareOptions.attempts || 0,
      hadCaption: !!customCaption,
    });
    
    window.open(facebookUrl, "_blank", "noopener,noreferrer,width=550,height=420");
  };

  const handleLinkedInShare = () => {
    const shareUrl = generateShareUrl(shareOptions.puzzleNumber);
    const linkedInUrl = generateLinkedInUrl(shareUrl);
    
    trackShareEvent('linkedin', {
      puzzleNumber: shareOptions.puzzleNumber,
      won: shareOptions.won || false,
      attempts: shareOptions.attempts || 0,
      hadCaption: !!customCaption,
    });
    
    window.open(linkedInUrl, "_blank", "noopener,noreferrer,width=550,height=420");
  };

  const handleWhatsAppShare = () => {
    const shareText = getShareTextWithCaption();
    const whatsappUrl = generateWhatsAppUrl(shareText);
    
    trackShareEvent('whatsapp', {
      puzzleNumber: shareOptions.puzzleNumber,
      won: shareOptions.won || false,
      attempts: shareOptions.attempts || 0,
      hadCaption: !!customCaption,
    });
    
    window.open(whatsappUrl, "_blank", "noopener,noreferrer");
  };

  const handleSMSShare = () => {
    const shareText = getShareTextWithCaption();
    const smsUrl = generateSMSUrl(shareText);
    
    trackShareEvent('sms', {
      puzzleNumber: shareOptions.puzzleNumber,
      won: shareOptions.won || false,
      attempts: shareOptions.attempts || 0,
      hadCaption: !!customCaption,
    });
    
    window.location.href = smsUrl;
  };

  const handleImageShare = async () => {
    if (!shareOptions.results || shareOptions.attempts === undefined) return;

    setIsGeneratingImage(true);
    try {
      const imageDataUrl = await generateShareImage({
        puzzleNumber: shareOptions.puzzleNumber,
        won: shareOptions.won || false,
        attempts: shareOptions.attempts,
        results: shareOptions.results,
      });

      const shared = await shareGeneratedImage(imageDataUrl, {
        puzzleNumber: shareOptions.puzzleNumber,
        won: shareOptions.won || false,
        attempts: shareOptions.attempts,
        results: shareOptions.results,
      });

      trackShareEvent('image', {
        puzzleNumber: shareOptions.puzzleNumber,
        won: shareOptions.won || false,
        attempts: shareOptions.attempts,
        hadCaption: !!customCaption,
      });

      if (!shared) {
        downloadShareImage(imageDataUrl, `wormble-${shareOptions.puzzleNumber}.png`);
        toast({
          title: "Image downloaded!",
          description: "Share it anywhere",
        });
      }
    } catch (err) {
      toast({
        title: "Failed to generate image",
        variant: "destructive",
      });
    } finally {
      setIsGeneratingImage(false);
    }
  };

  // Generate emoji grid preview
  const emojiGrid = shareOptions.results ? formatEmojiGrid(shareOptions.results) : "";

  const renderContent = () => (
    <div className="space-y-4 p-4">
      {/* Emoji Grid Preview */}
      {emojiGrid && (
        <div className="text-center py-3 bg-muted/30 rounded-lg">
          <pre className="text-lg leading-relaxed font-mono">{emojiGrid}</pre>
        </div>
      )}

      {/* Primary Share Actions - Clean Grid */}
      <div className="grid grid-cols-4 gap-2">
        <Button 
          onClick={handleCopy} 
          variant="outline" 
          size="sm"
          className="flex-col h-16 gap-1"
          aria-label="Copy results"
        >
          <Copy className="h-5 w-5" />
          <span className="text-xs">{copied ? "Copied!" : "Copy"}</span>
        </Button>
        
        <Button 
          onClick={handleWhatsAppShare} 
          variant="outline" 
          size="sm"
          className="flex-col h-16 gap-1"
          aria-label="Share on WhatsApp"
        >
          <MessageCircle className="h-5 w-5" />
          <span className="text-xs">WhatsApp</span>
        </Button>

        <Button 
          onClick={handleTwitterShare} 
          variant="outline" 
          size="sm"
          className="flex-col h-16 gap-1"
          aria-label="Share on X"
        >
          <XIcon className="h-5 w-5" />
          <span className="text-xs">X</span>
        </Button>

        <Button 
          onClick={handleSMSShare} 
          variant="outline" 
          size="sm"
          className="flex-col h-16 gap-1"
          aria-label="Share via SMS"
        >
          <MessageSquare className="h-5 w-5" />
          <span className="text-xs">SMS</span>
        </Button>
      </div>

      {/* Secondary Row */}
      <div className="grid grid-cols-4 gap-2">
        <Button 
          onClick={handleFacebookShare} 
          variant="outline" 
          size="sm"
          className="flex-col h-16 gap-1"
          aria-label="Share on Facebook"
        >
          <Facebook className="h-5 w-5" />
          <span className="text-xs">Facebook</span>
        </Button>

        <Button 
          onClick={handleLinkedInShare} 
          variant="outline" 
          size="sm"
          className="flex-col h-16 gap-1"
          aria-label="Share on LinkedIn"
        >
          <Linkedin className="h-5 w-5" />
          <span className="text-xs">LinkedIn</span>
        </Button>

        {shareOptions.results && (
          <Button 
            onClick={handleImageShare} 
            variant="outline" 
            size="sm"
            className="flex-col h-16 gap-1"
            aria-label="Share as image"
            disabled={isGeneratingImage}
          >
            {isGeneratingImage ? (
              <Loader2 className="h-5 w-5 animate-spin" />
            ) : (
              <ImageIcon className="h-5 w-5" />
            )}
            <span className="text-xs">Image</span>
          </Button>
        )}

        {capabilities.hasNativeShare && (
          <Button 
            onClick={handleNativeShare} 
            variant="outline" 
            size="sm"
            className="flex-col h-16 gap-1"
            aria-label="More share options"
          >
            <Share2 className="h-5 w-5" />
            <span className="text-xs">More</span>
          </Button>
        )}
      </div>

      {/* Advanced Options - Collapsible */}
      <Collapsible open={showAdvanced} onOpenChange={setShowAdvanced}>
        <CollapsibleTrigger asChild>
          <Button variant="ghost" size="sm" className="w-full text-muted-foreground">
            <ChevronDown className={`h-4 w-4 mr-2 transition-transform ${showAdvanced ? 'rotate-180' : ''}`} />
            {showAdvanced ? "Hide options" : "More options"}
          </Button>
        </CollapsibleTrigger>
        <CollapsibleContent className="space-y-3 pt-3">
          {shareOptions.attempts !== undefined && (
            <AICaptionGenerator
              guesses={shareOptions.attempts}
              won={shareOptions.won || false}
              streak={shareOptions.currentStreak || 0}
              puzzleNumber={shareOptions.puzzleNumber}
              onCaptionGenerated={setCustomCaption}
            />
          )}
          <ShareTroubleshoot compact />
        </CollapsibleContent>
      </Collapsible>
    </div>
  );

  if (isMobile) {
    return (
      <Drawer open={isOpen} onOpenChange={(open) => !open && onClose()}>
        <DrawerContent>
          <DrawerHeader className="pb-0">
            <DrawerTitle className="text-center">Share Your Result</DrawerTitle>
          </DrawerHeader>
          {renderContent()}
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-center">Share Your Result</DialogTitle>
        </DialogHeader>
        {renderContent()}
      </DialogContent>
    </Dialog>
  );
}
